import type { IChatContentItem } from './general-agent.types';

/** A summed token total — the shape the chat footer + capping surfaces consume. */
export interface ITokenUsage {
  inputTokens: number;
  outputTokens: number;
  // Whether the run this bucket came from was covered by the account's free allowance. Excluded from the
  // charged-token total shown beside the billing notice. Undefined means "not recorded" — an unknown run is
  // COUNTED, so buckets written before this field existed do not vanish from the total.
  underFreeUsage?: boolean;
}

const addUsage = (total: ITokenUsage, usage: ITokenUsage): ITokenUsage => ({
  inputTokens: total.inputTokens + usage.inputTokens,
  outputTokens: total.outputTokens + usage.outputTokens,
});

const EMPTY_USAGE: ITokenUsage = { inputTokens: 0, outputTokens: 0 };

// One agent run's token total. `cumulative_*` counters are per-model and monotonic WITHIN a run, so the latest
// value seen per model IS that model's run total; sum across models. Each user message spawns a NEW run whose
// counters reset — the caller buckets per run id, then sums the buckets for the session total (see sumTokenUsage
// in @artemis-ui/orion-chat). Shared so Open 360 and OrionIQ count identically.
export const sumRunTokenUsage = (chatContent: IChatContentItem[] = []): ITokenUsage => {
  const latestByModel = new Map<string, ITokenUsage>();

  for (const item of chatContent) {
    if (!item.usage_metadata) continue;

    for (const [model, usage] of Object.entries(item.usage_metadata)) {
      latestByModel.set(model, {
        inputTokens: usage.cumulative_input_tokens ?? 0,
        outputTokens: usage.cumulative_output_tokens ?? 0,
      });
    }
  }

  return [...latestByModel.values()].reduce(addUsage, EMPTY_USAGE);
};
